import type { Rule } from '@/lib/plan';

/**
 * Reminders driven by *property attributes* (basement, water source, sewer,
 * roof material, foundation, build year) rather than by a system row. These
 * complement task-rules.json — that file is the system-driven baseline,
 * this file is the "what I learn from the structure of your home" layer.
 *
 * Add new entries here freely. Each rule must have a unique `key` so the
 * dedup pass in regenerateTasksForProperty doesn't pile up duplicates.
 */
const attributeRules: Rule[] = [
  // — WATER & SEWER ——————————————————————————————————————————————
  {
    key: 'attr.well-water-annual-test',
    title: 'Annual well water test',
    why:
      'Private wells are not regulated. An annual test for coliform bacteria, nitrates and pH catches contamination early and protects everyone drinking from your tap.',
    icon: 'Droplets',
    tier: 'annual',
    season: 'spring',
    category: 'systems',
    time_minutes: 20,
    difficulty: 'easy',
    supplies: ['State-certified water test kit', 'Clean sample bottle'],
    steps: [
      'Order or pick up a state-certified test kit',
      'Collect samples per the kit instructions',
      'Mail or drop off at the lab; save the results PDF',
    ],
    tips: ['Test after any plumbing work or storm-related power loss too.'],
    applies_to: { water_sources: ['well'] },
  },
  {
    key: 'attr.well-pressure-tank-check',
    title: 'Check well pressure tank',
    why:
      'A waterlogged or undercharged pressure tank short-cycles the pump, which is the single biggest cause of premature well-pump failure.',
    icon: 'Gauge',
    tier: 'annual',
    season: 'fall',
    category: 'systems',
    time_minutes: 25,
    difficulty: 'moderate',
    applies_to: { water_sources: ['well'] },
  },
  {
    key: 'attr.septic-pump',
    title: 'Pump the septic tank',
    why:
      "Most septic tanks need pumping every 3–5 years. Skip it and solids reach your drain field — a $10,000+ repair instead of a $400 service call.",
    icon: 'Truck',
    tier: 'multi-year',
    years_between: 3,
    season: 'fall',
    category: 'systems',
    time_minutes: 90,
    difficulty: 'easy',
    tips: ["Keep your last pump-out invoice in Media — sets a clean baseline."],
    applies_to: { sewers: ['septic'] },
  },
  {
    key: 'attr.septic-leach-field-inspect',
    title: 'Walk the leach field',
    why:
      'Soggy ground, smell, or unusually green grass over the field is the early warning. A 5-minute walk twice a year catches problems before they back up indoors.',
    icon: 'MapPin',
    tier: 'biannual',
    season: 'spring',
    category: 'structure',
    time_minutes: 10,
    difficulty: 'easy',
    applies_to: { sewers: ['septic'] },
  },

  // — BASEMENT / FOUNDATION ————————————————————————————————————
  {
    key: 'attr.sump-pump-test',
    title: 'Test the sump pump',
    why:
      'Pour a bucket of water into the pit; pump should kick on within a few seconds. Cheapest insurance against a flooded basement during the next big storm.',
    icon: 'Droplet',
    tier: 'quarterly',
    season: 'any',
    category: 'systems',
    time_minutes: 10,
    difficulty: 'easy',
    supplies: ['5-gallon bucket'],
    steps: [
      'Lift the pit lid and remove visible debris',
      'Pour a full bucket of water in slowly',
      'Confirm the float rises and the pump engages',
      'Listen for grinding — that means impeller wear',
    ],
    applies_to: { basements: ['unfinished', 'finished', 'partial'] },
  },
  {
    key: 'attr.basement-humidity-check',
    title: 'Check basement humidity',
    why:
      'Above ~55% RH and you grow mold; below ~30% and your wood floors above suffer. A cheap hygrometer reading takes 30 seconds.',
    icon: 'Gauge',
    tier: 'quarterly',
    season: 'any',
    category: 'indoor',
    time_minutes: 5,
    difficulty: 'easy',
    applies_to: { basements: ['unfinished', 'finished', 'partial'] },
  },
  {
    key: 'attr.crawlspace-inspect',
    title: 'Crawl space walk-through',
    why:
      'Standing water, rodent droppings, vapor barrier tears and HVAC duct disconnects all live down here unnoticed. Spring is when problems show after winter freeze/thaw.',
    icon: 'Search',
    tier: 'biannual',
    season: 'spring',
    category: 'structure',
    time_minutes: 25,
    difficulty: 'moderate',
    applies_to: { foundations: ['crawl'], basements: ['crawl', 'none'] },
  },
  {
    key: 'attr.slab-perimeter-walk',
    title: 'Walk the foundation perimeter',
    why:
      'New cracks wider than a nickel, water staining at the base, or soil pulling away from the slab are all early signs worth catching before they grow.',
    icon: 'Footprints',
    tier: 'biannual',
    season: 'spring',
    category: 'structure',
    time_minutes: 15,
    difficulty: 'easy',
    applies_to: { foundations: ['slab', 'pier'] },
  },

  // — ROOF ————————————————————————————————————————————————————
  {
    key: 'attr.roof-asphalt-inspect',
    title: 'Roof inspection (asphalt)',
    why:
      'Curling tabs, exposed nails and granule loss in the gutter are the failure signals. Catching them at year 18 means a re-roof on your timeline; missing them means a leak on the roof\'s timeline.',
    icon: 'Home',
    tier: 'annual',
    season: 'fall',
    category: 'structure',
    time_minutes: 25,
    difficulty: 'moderate',
    applies_to: { roof_materials: ['asphalt'] },
  },
  {
    key: 'attr.roof-metal-fastener-check',
    title: 'Metal roof fastener check',
    why:
      'Metal panels last 40+ years but the fasteners and gasketed screws back out from thermal cycling. A 30-minute walk and re-torque keeps the seal.',
    icon: 'Hammer',
    tier: 'biannual',
    season: 'fall',
    category: 'structure',
    time_minutes: 45,
    difficulty: 'hard',
    applies_to: { roof_materials: ['metal'] },
  },
  {
    key: 'attr.roof-tile-walk',
    title: 'Tile roof walk-through',
    why:
      "Tile lasts 50+ years; the underlayment usually fails first. Spot cracked tiles and lifted ridge caps now so the underlayment isn't doing the work alone.",
    icon: 'Home',
    tier: 'annual',
    season: 'fall',
    category: 'structure',
    time_minutes: 35,
    difficulty: 'hard',
    applies_to: { roof_materials: ['tile', 'slate'] },
  },
  {
    key: 'attr.roof-flat-drain-clear',
    title: 'Clear flat-roof drains',
    why:
      'A flat roof with a blocked scupper is just a swimming pool waiting for the next storm. Costs nothing to clear; cost a lot if you miss it.',
    icon: 'Droplet',
    tier: 'quarterly',
    season: 'fall',
    category: 'structure',
    time_minutes: 20,
    difficulty: 'moderate',
    applies_to: { roof_materials: ['flat'] },
  },

  // — OLD HOMES (pre-1980) ————————————————————————————————————
  {
    key: 'attr.old-paint-inspect',
    title: 'Inspect for chipping paint',
    why:
      'Pre-1978 homes likely have lead-based paint somewhere. Chipping or peeling is the hazard — a quarterly walk-around catches it before it becomes dust.',
    icon: 'Brush',
    tier: 'quarterly',
    season: 'any',
    category: 'structure',
    time_minutes: 15,
    difficulty: 'easy',
    applies_to: { year_bands: ['pre60', '60-80'] },
  },
  {
    key: 'attr.old-electrical-panel-check',
    title: 'Look at the electrical panel',
    why:
      "Older homes sometimes have Federal Pacific or Zinsco panels that are fire risks. A photo of the panel label is enough for an electrician to tell you if you need a quote.",
    icon: 'Zap',
    tier: 'annual',
    season: 'any',
    category: 'systems',
    time_minutes: 10,
    difficulty: 'easy',
    tips: ['Snap a photo and save it to Media so you have it for any service call.'],
    applies_to: { year_bands: ['pre60', '60-80'] },
  },

  // — NEWER HOMES (smart-home era) ——————————————————————————
  {
    key: 'attr.smart-detectors-battery',
    title: 'Replace smoke/CO detector batteries',
    why:
      'Even hard-wired detectors have backup batteries. Twice-a-year (spring-forward / fall-back) is the easiest cadence to remember.',
    icon: 'BellRing',
    tier: 'biannual',
    season: 'spring',
    category: 'indoor',
    time_minutes: 15,
    difficulty: 'easy',
    applies_to: { year_bands: ['90-00', '2010+'] },
  },

  // — POOL ————————————————————————————————————————————————————
  {
    key: 'attr.pool-chemistry-weekly',
    title: 'Test pool water chemistry',
    why:
      'Free chlorine, pH and alkalinity drift fast in season. A 5-minute strip test once a week is the difference between clear water and a $300 shock-and-clear weekend.',
    icon: 'Droplet',
    tier: 'weekly',
    season: 'summer',
    category: 'outdoor',
    time_minutes: 10,
    difficulty: 'easy',
    supplies: ['Test strips or kit'],
    applies_to: { has_pool: true },
  },
  {
    key: 'attr.pool-skim-vacuum',
    title: 'Skim and vacuum the pool',
    why:
      "Debris on the bottom turns into stains and feeds algae. Twenty minutes a week keeps the chemistry stable so the chlorine isn't fighting biomass instead of bacteria.",
    icon: 'Wind',
    tier: 'weekly',
    season: 'summer',
    category: 'outdoor',
    time_minutes: 25,
    difficulty: 'easy',
    applies_to: { has_pool: true },
  },
  {
    key: 'attr.pool-winterize',
    title: 'Winterize the pool',
    why:
      'Lower water level, blow out the lines, dose the closing chemicals and cover it. Skipping this is how cracked plumbing and a green spring opening happen.',
    icon: 'Snowflake',
    tier: 'annual',
    season: 'fall',
    category: 'outdoor',
    time_minutes: 90,
    difficulty: 'moderate',
    // Fires ~10 days before first frost so the user has buffer for an
    // early cold snap. Pools without water in the lines survive
    // unexpected freezes — pools mid-winterization don't.
    keyed_event: 'first_frost',
    days_offset: -10,
    // Skip in hot zones where pools generally stay open year-round —
    // FL / TX / AZ pool owners don't close their pools and a
    // "winterize" reminder reads as wrong/spammy.
    applies_to: {
      has_pool: true,
      climate_zones: ['cold-humid', 'cold-dry', 'mixed-humid', 'mixed-dry', 'marine'],
    },
  },
  {
    key: 'attr.pool-filter-clean',
    title: 'Clean the pool filter',
    why:
      'Pressure 8–10 psi over baseline = clogged. A clogged filter strains the pump and starves the heater.',
    icon: 'Filter',
    tier: 'monthly',
    season: 'summer',
    category: 'systems',
    time_minutes: 30,
    difficulty: 'moderate',
    applies_to: { has_pool: true },
  },

  // — SOLAR ————————————————————————————————————————————————————
  {
    key: 'attr.solar-panels-wash',
    title: 'Wash the solar panels',
    why:
      'Dust + pollen reduces output 5-15%. A spring rinse before peak production season pays for itself in a few weeks of generation.',
    icon: 'Sun',
    tier: 'annual',
    season: 'spring',
    category: 'systems',
    time_minutes: 45,
    difficulty: 'moderate',
    tips: ["Cool panels, soft brush, deionized water. Don't walk on them."],
    applies_to: { has_solar: true },
  },
  {
    key: 'attr.solar-inverter-check',
    title: 'Read the inverter / monitoring app',
    why:
      "Most outages are silent until your bill jumps. A 60-second check of yesterday's production vs. expected catches dead strings or microinverters early.",
    icon: 'Activity',
    tier: 'monthly',
    season: 'any',
    category: 'systems',
    time_minutes: 5,
    difficulty: 'easy',
    applies_to: { has_solar: true },
  },

  // — PETS ————————————————————————————————————————————————————
  {
    key: 'attr.pets-hvac-filter-extra',
    title: 'Mid-cycle HVAC filter check',
    why:
      'Pet dander loads filters 2-3x faster than expected. A 30-second visual halfway between scheduled changes prevents a starved blower motor.',
    icon: 'Wind',
    tier: 'monthly',
    season: 'any',
    category: 'systems',
    time_minutes: 5,
    difficulty: 'easy',
    applies_to: { has_pets: true },
  },
  {
    key: 'attr.pets-vacuum-vents',
    title: 'Vacuum supply registers',
    why:
      'Pet hair builds up at the register fins faster than anywhere else in the duct system. Quarterly vacuuming makes the whole-house duct cleaning service less necessary.',
    icon: 'Brush',
    tier: 'quarterly',
    season: 'any',
    category: 'indoor',
    time_minutes: 15,
    difficulty: 'easy',
    applies_to: { has_pets: true },
  },
  {
    key: 'attr.pets-yard-pickup',
    title: 'Yard cleanup (pet waste)',
    why:
      'Beyond the obvious: pet waste raises bacterial load in stormwater. Most municipalities have an ordinance you can be fined under.',
    icon: 'Trash2',
    tier: 'weekly',
    season: 'any',
    category: 'outdoor',
    time_minutes: 15,
    difficulty: 'easy',
    applies_to: { has_pets: true },
  },

  // — KIDS AT HOME ——————————————————————————————————————————
  {
    key: 'attr.kids-water-temp',
    title: 'Verify water heater temperature ≤ 120°F',
    why:
      'Scald risk doubles every 5° above 120°F. CPSC and AAP both recommend 120°F max in homes with young children — annual check is plenty.',
    icon: 'Thermometer',
    tier: 'annual',
    season: 'any',
    category: 'systems',
    time_minutes: 10,
    difficulty: 'easy',
    applies_to: { has_kids: true },
  },
  {
    key: 'attr.kids-outlet-covers',
    title: 'Walk the house for missing outlet covers',
    why:
      "Covers get pulled out, vacuum cords snag them, kids find the loose one. Quarterly walk-through catches them before they're a danger.",
    icon: 'PlugZap',
    tier: 'quarterly',
    season: 'any',
    category: 'indoor',
    time_minutes: 10,
    difficulty: 'easy',
    applies_to: { has_kids: true },
  },
  {
    key: 'attr.kids-medicine-cabinet-audit',
    title: 'Medicine cabinet audit',
    why:
      'Expired meds + curious kids = the most common preventable poisoning. Quarterly purge keeps the cabinet honest.',
    icon: 'Stethoscope',
    tier: 'quarterly',
    season: 'any',
    category: 'indoor',
    time_minutes: 15,
    difficulty: 'easy',
    applies_to: { has_kids: true },
  },

  // — HOA —————————————————————————————————————————————————————
  {
    key: 'attr.hoa-dues-check',
    title: 'Verify HOA dues are current',
    why:
      'HOA late fees are punitive and compounding. A quick login or check of the autopay statement once a quarter catches a lapsed card or skipped month.',
    icon: 'PiggyBank',
    tier: 'quarterly',
    season: 'any',
    category: 'systems',
    time_minutes: 5,
    difficulty: 'easy',
    applies_to: { has_hoa: true },
  },
  {
    key: 'attr.hoa-rule-refresh',
    title: 'Skim the HOA architectural / yard rules',
    why:
      'HOA rules quietly update. A 10-minute annual skim before paint, fence, or landscaping work prevents a stop-work order or a fine.',
    icon: 'FileText',
    tier: 'annual',
    season: 'spring',
    category: 'structure',
    time_minutes: 10,
    difficulty: 'easy',
    applies_to: { has_hoa: true },
  },

  // — WOOD STOVE / FIREPLACE ————————————————————————————————
  {
    key: 'attr.chimney-sweep',
    title: 'Chimney sweep + inspection',
    why:
      'Creosote builds up with every burn. NFPA recommends inspection annually; sweep when there is more than 1/8" of buildup. Fires kill 100+ Americans a year from preventable chimney issues.',
    icon: 'Flame',
    tier: 'annual',
    season: 'fall',
    category: 'systems',
    time_minutes: 90,
    difficulty: 'hard',
    // ~30 days before first frost — book the sweep before peak season
    // demand makes scheduling 6 weeks out a thing.
    keyed_event: 'first_frost',
    days_offset: -30,
    applies_to: { has_woodfire: true },
  },
  {
    key: 'attr.firewood-stack',
    title: 'Stock + cover next season’s firewood',
    why:
      'Wood needs 6+ months seasoning to burn clean. Late summer stack means dry wood by first frost; covered means it stays that way.',
    icon: 'TreePine',
    tier: 'annual',
    season: 'summer',
    category: 'outdoor',
    time_minutes: 90,
    difficulty: 'moderate',
    applies_to: { has_woodfire: true },
  },
  {
    key: 'attr.ash-empty',
    title: 'Empty the ash pan / bucket',
    why:
      'Hot ash + plastic bucket = house fire. Use a metal can with a lid, keep it outside on bare ground or concrete for 3+ days before disposal.',
    icon: 'Trash2',
    tier: 'weekly',
    season: 'winter',
    category: 'indoor',
    time_minutes: 10,
    difficulty: 'easy',
    applies_to: { has_woodfire: true },
  },

  // — SMART HOME ————————————————————————————————————————————
  {
    key: 'attr.smart-sensor-batteries',
    title: 'Swap sensor batteries',
    why:
      'Door / motion / leak sensors silently die. A coordinated annual swap (cheapest in 24-packs) beats chasing them one at a time when they ghost.',
    icon: 'Battery',
    tier: 'annual',
    season: 'spring',
    category: 'systems',
    time_minutes: 30,
    difficulty: 'easy',
    applies_to: { has_smart_home: true },
  },
  {
    key: 'attr.smart-hub-firmware',
    title: 'Update smart-home hub firmware',
    why:
      'Hubs ship security patches quietly. A quarterly check on Home Assistant / SmartThings / Hubitat keeps the home network safer.',
    icon: 'Webhook',
    tier: 'quarterly',
    season: 'any',
    category: 'systems',
    time_minutes: 10,
    difficulty: 'easy',
    applies_to: { has_smart_home: true },
  },

  // — SHORT-TERM RENTAL HOST ——————————————————————————————
  {
    key: 'attr.str-deep-clean',
    title: 'Deep-clean turnover audit',
    why:
      "Guest reviews live and die on the first 60 seconds. Quarterly deep clean — under beds, oven, drip pans — catches the stuff your turnover crew skips.",
    icon: 'Brush',
    tier: 'quarterly',
    season: 'any',
    category: 'indoor',
    time_minutes: 120,
    difficulty: 'moderate',
    applies_to: { has_str_host: true },
  },
  {
    key: 'attr.str-supply-restock',
    title: 'Restock guest supplies',
    why:
      'Coffee, paper towels, dish tabs, batteries, light bulbs, fire extinguisher charge. Running out at 11 PM on a Friday means a 1-star review.',
    icon: 'Package',
    tier: 'monthly',
    season: 'any',
    category: 'indoor',
    time_minutes: 30,
    difficulty: 'easy',
    applies_to: { has_str_host: true },
  },
  {
    key: 'attr.str-smoke-co-test',
    title: 'Test guest-area smoke + CO alarms',
    why:
      'Most short-term rental platforms require working alarms; some insurance riders too. A monthly press-and-hold confirms before a guest reports them.',
    icon: 'BellRing',
    tier: 'monthly',
    season: 'any',
    category: 'systems',
    time_minutes: 5,
    difficulty: 'easy',
    applies_to: { has_str_host: true },
  },

  // — HEAVY SNOW REGION ——————————————————————————————————
  {
    key: 'attr.snow-gutter-prep',
    title: 'Clear gutters before first freeze',
    why:
      'Clogged gutters + freeze = ice dams + interior water damage. A late-fall clean is the single best ice-dam prevention.',
    icon: 'Droplet',
    tier: 'annual',
    season: 'fall',
    category: 'structure',
    time_minutes: 60,
    difficulty: 'moderate',
    // ~2 weeks before first frost — the gutters have already collected
    // leaves but the weather is still mild enough to be on a ladder.
    keyed_event: 'first_frost',
    days_offset: -14,
    applies_to: { has_heavy_snow: true },
  },
  {
    key: 'attr.snow-roof-check',
    title: 'Check roof snow load after big storms',
    why:
      'Most residential roofs handle 20-30 psf — about 4 ft of fresh snow. Heavy wet snow doubles that weight. Rake from a long-handle tool, never from on top.',
    icon: 'Snowflake',
    tier: 'monthly',
    season: 'winter',
    category: 'structure',
    time_minutes: 20,
    difficulty: 'moderate',
    // Belt-and-suspenders: gated by lifestyle flag AND climate. A user
    // who toggled has_heavy_snow but lives in Phoenix shouldn't get
    // monthly snow-load reminders. Both filters must pass.
    applies_to: {
      has_heavy_snow: true,
      climate_zones: ['cold-humid', 'cold-dry', 'mixed-humid', 'marine'],
    },
  },
  {
    key: 'attr.snow-blower-service',
    title: 'Pre-season snow blower test',
    why:
      'Discovering your snow blower won’t start at 5 AM in 14" of fresh is a special kind of bad. A November test run catches fuel + carb issues with time to fix them.',
    icon: 'Wrench',
    tier: 'annual',
    season: 'fall',
    category: 'systems',
    time_minutes: 30,
    difficulty: 'easy',
    // ~30 days before first frost — plenty of time to fix a no-start
    // before the first real storm.
    keyed_event: 'first_frost',
    days_offset: -30,
    applies_to: { has_heavy_snow: true },
  },

  // — GARDEN / VEGETABLE BEDS ———————————————————————————
  {
    key: 'attr.garden-soil-test',
    title: 'Soil test (pH + NPK)',
    why:
      "Cheap mail-in or county-extension test. Fertilizer guesswork wastes money; a real soil test pays for itself in the first season.",
    icon: 'FlaskConical',
    tier: 'annual',
    season: 'spring',
    category: 'outdoor',
    time_minutes: 20,
    difficulty: 'easy',
    applies_to: { has_garden: true },
  },
  {
    key: 'attr.garden-bed-prep',
    title: 'Bed prep for the growing season',
    why:
      'Compost, mulch, irrigation check, weed cloth. A focused weekend in early spring saves dozens of frantic afternoons in July.',
    icon: 'Sprout',
    tier: 'annual',
    season: 'spring',
    category: 'outdoor',
    time_minutes: 180,
    difficulty: 'moderate',
    // 2 weeks BEFORE last frost — soil is workable, but you're still
    // covering tender starts on cold nights. The right window for
    // amendments + bed shape, not for planting tender annuals.
    keyed_event: 'last_frost',
    days_offset: -14,
    applies_to: { has_garden: true },
  },
  {
    key: 'attr.garden-end-of-season',
    title: 'End-of-season garden cleanup',
    why:
      'Pull spent plants, cover crops in beds you’re fallowing, drain hoses, winterize drip lines. Sets next spring up for success.',
    icon: 'Leaf',
    tier: 'annual',
    season: 'fall',
    category: 'outdoor',
    time_minutes: 120,
    difficulty: 'moderate',
    // ~1 week AFTER first frost — most annuals are blackened, cleanup
    // is obvious. Drip lines need to drain before any hard freeze.
    keyed_event: 'first_frost',
    days_offset: 7,
    applies_to: { has_garden: true },
  },

  // — ACCESSIBILITY / AGING IN PLACE ————————————————————
  {
    key: 'attr.access-grab-bar-check',
    title: 'Test grab bars + handrails',
    why:
      'Loose grab bars are worse than none — they give a false sense of security right when balance fails. Push hard on every one twice a year.',
    icon: 'HandHelping',
    tier: 'biannual',
    season: 'spring',
    category: 'indoor',
    time_minutes: 10,
    difficulty: 'easy',
    applies_to: { has_accessibility: true },
  },
  {
    key: 'attr.access-fall-hazard-walk',
    title: 'Fall-hazard walk-through',
    why:
      'Trip hazards multiply quietly — a curled rug edge, a new extension cord, a loose step. A quarterly walk catches them before someone goes down.',
    icon: 'Footprints',
    tier: 'quarterly',
    season: 'any',
    category: 'indoor',
    time_minutes: 15,
    difficulty: 'easy',
    applies_to: { has_accessibility: true },
  },

  // — FIRST-TIME HOMEOWNER ——————————————————————————————
  {
    key: 'attr.first-time-shutoff-tour',
    title: 'Learn your shutoffs',
    why:
      'Water main, gas main, breaker panel, sprinkler. When something fails at 2 AM you want muscle memory, not Googling. Do this in the first month.',
    icon: 'Power',
    tier: 'annual',
    season: 'any',
    category: 'systems',
    time_minutes: 30,
    difficulty: 'easy',
    applies_to: { is_first_time: true },
  },
  // — UNIVERSAL SUMMER ESSENTIALS ————————————————————————
  // Summer was also under-represented (11 rules across the whole
  // library). These four apply to every home so June-August has
  // something to do without leaning on lifestyle flags.
  {
    key: 'attr.summer-ac-filter-midcycle',
    title: 'Mid-summer A/C filter swap',
    why:
      'Cooling load peaks in July; a fresh filter restores 5-10% efficiency and prevents short-cycling. 60 seconds and a $10 filter.',
    icon: 'Filter',
    tier: 'annual',
    season: 'summer',
    category: 'systems',
    time_minutes: 10,
    difficulty: 'easy',
    applies_to: {},
  },
  {
    key: 'attr.summer-standing-water-walk',
    title: 'Eliminate standing water',
    why:
      'Mosquitoes breed in any puddle bigger than a bottle cap. Walk the yard weekly in summer: empty bird baths, tarps, kid toys, blocked gutters, plant saucers.',
    icon: 'Droplets',
    tier: 'annual',
    season: 'summer',
    category: 'outdoor',
    time_minutes: 20,
    difficulty: 'easy',
    applies_to: {},
  },
  {
    key: 'attr.summer-outdoor-faucet-check',
    title: 'Inspect outdoor faucets + hoses',
    why:
      'A slow-drip hose bib left running overnight wastes thousands of gallons. Tighten washers, check for cracks, verify each spigot shuts off cleanly.',
    icon: 'Wrench',
    tier: 'annual',
    season: 'summer',
    category: 'outdoor',
    time_minutes: 15,
    difficulty: 'easy',
    applies_to: {},
  },
  {
    key: 'attr.summer-deck-walk',
    title: 'Walk the deck / patio for loose boards',
    why:
      'Heat + humidity + foot traffic pop nails and split planks. A 10-minute summer walk catches splinters and sag before someone gets hurt.',
    icon: 'Footprints',
    tier: 'annual',
    season: 'summer',
    category: 'outdoor',
    time_minutes: 10,
    difficulty: 'easy',
    applies_to: {},
  },

  // — UNIVERSAL WINTER ESSENTIALS ————————————————————————
  // Winter was massively under-represented in the catalog (5 rules
  // across the whole library). These 5 generic universal rules apply
  // to every home regardless of attributes so every user has at least
  // ~10 winter tasks distributed across Dec-Feb.
  {
    key: 'attr.winter-thermostat-program',
    title: 'Program winter thermostat schedule',
    why:
      'Set heat down 5-10°F when you sleep or are out. Average household saves 5-15% on heating without losing comfort. A 30-second tune once a year.',
    icon: 'Thermometer',
    tier: 'annual',
    season: 'winter',
    category: 'systems',
    time_minutes: 10,
    difficulty: 'easy',
    applies_to: {},
  },
  {
    key: 'attr.winter-window-draft-walk',
    title: 'Hunt for window + door drafts',
    why:
      'A January walk with a stick of incense (or just a wet hand) finds every drafty seal. Weatherstrip or caulk now — the energy savings start within the month.',
    icon: 'Wind',
    tier: 'annual',
    season: 'winter',
    category: 'structure',
    time_minutes: 45,
    difficulty: 'easy',
    applies_to: {},
  },
  {
    key: 'attr.winter-humidity-check',
    title: 'Check indoor humidity',
    why:
      'Heating systems dry the air. Below 30% RH means itchy skin, static shocks, and split wood floors. A $20 hygrometer + a small humidifier hold 35-45%.',
    icon: 'Droplet',
    tier: 'annual',
    season: 'winter',
    category: 'indoor',
    time_minutes: 15,
    difficulty: 'easy',
    applies_to: {},
  },
  {
    key: 'attr.winter-emergency-kit',
    title: 'Refresh winter emergency kit',
    why:
      'Flashlight batteries, candles, blankets, bottled water, non-perishable snacks. One bin in the closet means a winter power outage is annoying, not dangerous.',
    icon: 'Backpack',
    tier: 'annual',
    season: 'winter',
    category: 'systems',
    time_minutes: 30,
    difficulty: 'easy',
    applies_to: {},
  },
  {
    key: 'attr.winter-pipe-insulation-check',
    title: 'Inspect exposed pipes for insulation',
    why:
      'Garage, crawlspace, exterior-wall plumbing. Pre-formed foam sleeves are $1/ft at any hardware store and prevent a $3000 burst-pipe disaster on the next polar vortex.',
    icon: 'ShieldCheck',
    tier: 'annual',
    season: 'winter',
    category: 'structure',
    time_minutes: 30,
    difficulty: 'easy',
    applies_to: {},
  },

  {
    key: 'attr.first-time-doc-binder',
    title: 'Build a home document binder',
    why:
      'Mortgage, insurance, survey, warranties, inspection report. Use the Media tool — having everything in one place pays off the first time you need to dig anything up.',
    icon: 'Briefcase',
    tier: 'annual',
    season: 'any',
    category: 'systems',
    time_minutes: 45,
    difficulty: 'easy',
    applies_to: { is_first_time: true },
  },

  // — TRAVELS OFTEN ————————————————————————————————————————
  {
    key: 'attr.travel-pre-trip-checklist',
    title: 'Pre-trip home checklist',
    why:
      'Water main partially off, thermostat set back, fridge cleaned, lights on timers, mail held. Five minutes of prep prevents the $5,000 vacation horror story.',
    icon: 'Plane',
    tier: 'quarterly',
    season: 'any',
    category: 'systems',
    time_minutes: 15,
    difficulty: 'easy',
    steps: [
      'Shut off the water main (or at minimum the washer + dishwasher supplies)',
      'Set thermostat back, but not so far it allows freeze in winter',
      'Empty fridge of anything that will spoil',
      'Lights on timers + a porch light always on',
      'Hold mail / forward packages',
    ],
    applies_to: { travels_often: true },
  },
  {
    key: 'attr.travel-leak-monitor',
    title: 'Verify leak sensors before traveling',
    why:
      'Smart leak sensors only help if they have battery + Wi-Fi. A 60-second walk-around before a trip is the cheapest insurance you have.',
    icon: 'Droplet',
    tier: 'quarterly',
    season: 'any',
    category: 'systems',
    time_minutes: 10,
    difficulty: 'easy',
    applies_to: { travels_often: true },
  },
  {
    key: 'attr.travel-winter-freeze-plan',
    title: 'Winter trip: freeze-protection plan',
    why:
      'Frozen pipes burst when you’re gone for 3+ days in a cold snap. Keep heat at 55°F+, open cabinet doors under sinks on exterior walls, and have a neighbor on call.',
    icon: 'Snowflake',
    tier: 'annual',
    season: 'winter',
    category: 'systems',
    time_minutes: 20,
    difficulty: 'easy',
    applies_to: { travels_often: true },
  },
  {
    key: 'attr.travel-return-walkthrough',
    title: 'Post-trip walkthrough',
    why:
      'Smell for gas, peek at the water heater pan, check sump pit, look for ceiling stains. Five minutes after a trip catches anything that slipped past the sensors.',
    icon: 'ClipboardCheck',
    tier: 'quarterly',
    season: 'any',
    category: 'structure',
    time_minutes: 10,
    difficulty: 'easy',
    applies_to: { travels_often: true },
  },
  {
    key: 'attr.travel-trusted-contact-list',
    title: 'Refresh your trusted-contact list',
    why:
      'Neighbor, plumber, electrician, alarm company, vet, pet sitter. When you’re 2,000 miles away, the difference between a fix and a disaster is having the right number ready.',
    icon: 'Phone',
    tier: 'annual',
    season: 'any',
    category: 'systems',
    time_minutes: 15,
    difficulty: 'easy',
    applies_to: { travels_often: true },
  },

  // — WORKS FROM HOME ——————————————————————————————————————
  {
    key: 'attr.wfh-hvac-filter-midcycle',
    title: 'Mid-cycle HVAC filter swap',
    why:
      'You’re breathing this air 10+ hours a day. Bumping the filter cadence up — between regular system reminders — keeps dust + VOCs down where they belong.',
    icon: 'Wind',
    tier: 'quarterly',
    season: 'any',
    category: 'systems',
    time_minutes: 10,
    difficulty: 'easy',
    applies_to: { works_from_home: true },
  },
  {
    key: 'attr.wfh-indoor-air-quality',
    title: 'Indoor air quality check',
    why:
      'CO2 above 1,000 ppm tanks focus. A $40 CO2 monitor or smart sensor on your desk tells you if your office needs more ventilation or a HEPA bump.',
    icon: 'Activity',
    tier: 'annual',
    season: 'any',
    category: 'indoor',
    time_minutes: 30,
    difficulty: 'easy',
    applies_to: { works_from_home: true },
  },
  {
    key: 'attr.wfh-desk-area-alarms',
    title: 'Test the smoke + CO alarm in your work area',
    why:
      'You spend more waking hours in your home office than anywhere else. The alarm closest to that chair gets the press-and-hold treatment every quarter.',
    icon: 'BellRing',
    tier: 'quarterly',
    season: 'any',
    category: 'systems',
    time_minutes: 5,
    difficulty: 'easy',
    applies_to: { works_from_home: true },
  },
  {
    key: 'attr.wfh-ups-runtime-test',
    title: 'Test UPS / battery backup runtime',
    why:
      'A 5-year-old UPS battery may hold under 90 seconds. Unplug it briefly once a year — if it can’t hold your gear for a graceful shutdown, swap the battery.',
    icon: 'BatteryCharging',
    tier: 'annual',
    season: 'fall',
    category: 'systems',
    time_minutes: 15,
    difficulty: 'easy',
    applies_to: { works_from_home: true },
  },

  // — HAS ALLERGIES ————————————————————————————————————————
  {
    key: 'attr.allergy-merv13-upgrade',
    title: 'Upgrade to MERV-13 HVAC filters',
    why:
      'MERV-13 catches pollen, mold spores, and most pet dander at the system level. Confirm your HVAC can handle the static pressure before swapping permanently.',
    icon: 'Filter',
    tier: 'annual',
    season: 'spring',
    category: 'systems',
    time_minutes: 15,
    difficulty: 'easy',
    applies_to: { has_allergies: true },
  },
  {
    key: 'attr.allergy-hepa-filter-swap',
    title: 'Replace HEPA purifier filters',
    why:
      'Once HEPA filters load up they restrict airflow without trapping more particles. Most need replacement every 6–12 months — sooner during heavy allergy season.',
    icon: 'Wind',
    tier: 'biannual',
    season: 'spring',
    category: 'indoor',
    time_minutes: 15,
    difficulty: 'easy',
    applies_to: { has_allergies: true },
  },
  {
    key: 'attr.allergy-duct-cleaning',
    title: 'Professional duct cleaning',
    why:
      'Worth doing every 3–5 years if the household has allergies, especially after a renovation or new pets. Get before/after photos so you know the crew actually did the work.',
    icon: 'Wind',
    tier: 'multi-year',
    years_between: 4,
    season: 'spring',
    category: 'systems',
    time_minutes: 30,
    difficulty: 'easy',
    applies_to: { has_allergies: true },
  },
  {
    key: 'attr.allergy-vacuum-hepa',
    title: 'Service the HEPA vacuum',
    why:
      'A vacuum without a sealed HEPA bag puts the fine dust straight back into the air. Replace the bag + check the seals annually — and confirm post-motor filters too.',
    icon: 'Wrench',
    tier: 'annual',
    season: 'spring',
    category: 'indoor',
    time_minutes: 15,
    difficulty: 'easy',
    applies_to: { has_allergies: true },
  },
  {
    key: 'attr.allergy-dehumidifier-summer',
    title: 'Run dehumidifier in damp months',
    why:
      'Dust mites and mold thrive above 50% RH. A basement or bedroom dehumidifier set to 45–50% in summer is one of the highest-ROI allergy interventions.',
    icon: 'Droplet',
    tier: 'annual',
    season: 'summer',
    category: 'indoor',
    time_minutes: 10,
    difficulty: 'easy',
    applies_to: { has_allergies: true },
  },

  // — HAS NEWBORN ——————————————————————————————————————————
  {
    key: 'attr.newborn-humidity-check',
    title: 'Hold nursery humidity 40–60%',
    why:
      'Infant airways are especially sensitive to dry air; below 30% RH means more congestion and disrupted sleep. A $20 hygrometer + a cool-mist humidifier is the fix.',
    icon: 'Droplet',
    tier: 'monthly',
    season: 'winter',
    category: 'indoor',
    time_minutes: 10,
    difficulty: 'easy',
    applies_to: { has_newborn: true },
  },
  {
    key: 'attr.newborn-leadpaint-check',
    title: 'Check for chipping paint (pre-1978 homes)',
    why:
      'Lead paint is the single biggest hidden risk in older homes with crawling babies. Photograph any chipping or chalking near floors/windowsills and budget for proper abatement.',
    icon: 'ShieldAlert',
    tier: 'biannual',
    season: 'spring',
    category: 'indoor',
    time_minutes: 20,
    difficulty: 'easy',
    applies_to: { has_newborn: true, year_bands: ['pre60', '60-80'] },
  },
  {
    key: 'attr.newborn-smoke-co-bedside',
    title: 'Verify smoke + CO alarm near nursery',
    why:
      'Code requires one inside each sleeping room. Press-and-hold quarterly — and put a CO alarm within 10 ft of the nursery door if you have any combustion appliance.',
    icon: 'BellRing',
    tier: 'quarterly',
    season: 'any',
    category: 'systems',
    time_minutes: 5,
    difficulty: 'easy',
    applies_to: { has_newborn: true },
  },
  {
    key: 'attr.newborn-water-heater-120',
    title: 'Drop water heater to 120°F',
    why:
      'At 140°F a baby gets a third-degree scald in under a second. 120°F still kills bacteria and is the safe family setting.',
    icon: 'Thermometer',
    tier: 'annual',
    season: 'any',
    category: 'systems',
    time_minutes: 10,
    difficulty: 'easy',
    applies_to: { has_newborn: true },
  },
  {
    key: 'attr.newborn-anti-scald-tub',
    title: 'Install or test anti-scald tub valve',
    why:
      'Pressure-balancing valves prevent the temperature spike when a toilet flushes mid-bath. Either confirm yours is rated, or budget the swap before bath time gets routine.',
    icon: 'ShieldCheck',
    tier: 'annual',
    season: 'any',
    category: 'systems',
    time_minutes: 15,
    difficulty: 'easy',
    applies_to: { has_newborn: true },
  },

  // — MULTI-GENERATIONAL HOUSEHOLD ————————————————————————
  {
    key: 'attr.multigen-inlaw-walkthrough',
    title: 'In-law unit / ADU walkthrough',
    why:
      'Secondary units share fewer of your daily eyes. A scheduled walkthrough — smoke alarm, faucet drips, GFCI test, range hood filter — catches the slow stuff.',
    icon: 'Home',
    tier: 'quarterly',
    season: 'any',
    category: 'structure',
    time_minutes: 30,
    difficulty: 'easy',
    applies_to: { multi_gen_household: true },
  },
  {
    key: 'attr.multigen-second-water-heater',
    title: 'Maintain the secondary water heater',
    why:
      'A second unit usually means a second water heater. It needs the same anode + flush schedule — and is the one most likely to be quietly leaking into a cabinet for months.',
    icon: 'Wrench',
    tier: 'annual',
    season: 'fall',
    category: 'systems',
    time_minutes: 30,
    difficulty: 'moderate',
    applies_to: { multi_gen_household: true },
  },
  {
    key: 'attr.multigen-hvac-zones',
    title: 'Balance HVAC zones for comfort',
    why:
      'Different generations want different temperatures. Adjusting dampers in the off-season — and confirming each zone’s thermostat reads true — heads off the thermostat wars.',
    icon: 'Thermometer',
    tier: 'biannual',
    season: 'spring',
    category: 'systems',
    time_minutes: 30,
    difficulty: 'moderate',
    applies_to: { multi_gen_household: true },
  },
  {
    key: 'attr.multigen-grab-bars',
    title: 'Add or check grab bars',
    why:
      'A multi-gen household usually means at least one fall-risk resident. Grab bars in tub + toilet + by exterior steps — and a quarterly push-test on each.',
    icon: 'HandHelping',
    tier: 'biannual',
    season: 'spring',
    category: 'indoor',
    time_minutes: 15,
    difficulty: 'easy',
    applies_to: { multi_gen_household: true },
  },

  // — OVER-60 OWNER ————————————————————————————————————————
  {
    key: 'attr.over60-skip-the-ladder',
    title: 'Hire out the ladder work',
    why:
      'Falls from ladders are the #1 home-injury hospitalization for owners over 60. Gutters, high light bulbs, smoke alarms — pay someone. Cheaper than a hip.',
    icon: 'ShieldAlert',
    tier: 'annual',
    season: 'fall',
    category: 'systems',
    time_minutes: 10,
    difficulty: 'easy',
    applies_to: { over_60_owner: true },
  },
  {
    key: 'attr.over60-handrail-tighten',
    title: 'Tighten every handrail in the house',
    why:
      'Wood swells and shrinks; screws loosen. Once a year, a 5-minute pass with a screwdriver on every stair + porch + bath rail buys real fall protection.',
    icon: 'Wrench',
    tier: 'annual',
    season: 'spring',
    category: 'structure',
    time_minutes: 20,
    difficulty: 'easy',
    applies_to: { over_60_owner: true },
  },
  {
    key: 'attr.over60-nightlight-audit',
    title: 'Nightlight + path-lighting audit',
    why:
      'Most nighttime falls happen on the path from bed to bathroom. Plug-in nightlights with auto-on photocells in hallway, bath, and any stair — replace dead ones now.',
    icon: 'Lightbulb',
    tier: 'biannual',
    season: 'fall',
    category: 'indoor',
    time_minutes: 15,
    difficulty: 'easy',
    applies_to: { over_60_owner: true },
  },
  {
    key: 'attr.over60-fall-hazard-walk',
    title: 'Walk the house for fall hazards',
    why:
      'Loose rugs, frayed cords across walkways, slick bath mats, low-contrast steps. One slow lap with fresh eyes — or a CAPABLE friend’s eyes — catches more than you think.',
    icon: 'Footprints',
    tier: 'biannual',
    season: 'spring',
    category: 'indoor',
    time_minutes: 30,
    difficulty: 'easy',
    applies_to: { over_60_owner: true },
  },
  {
    key: 'attr.over60-anti-scald-check',
    title: 'Confirm water heater is at 120°F',
    why:
      'Aging skin scalds faster. 120°F protects without inviting bacteria. Confirm with a kitchen thermometer at the tap, not just the dial setting.',
    icon: 'Thermometer',
    tier: 'annual',
    season: 'any',
    category: 'systems',
    time_minutes: 10,
    difficulty: 'easy',
    applies_to: { over_60_owner: true },
  },

  // — HAS LIVESTOCK ————————————————————————————————————————
  {
    key: 'attr.livestock-trough-heater',
    title: 'Service the trough heater before first freeze',
    why:
      'A failed trough heater = ice-bound water in 18 hours. Test before you need it, inspect the cord for rodent damage, and have a spare on the shelf.',
    icon: 'Flame',
    tier: 'annual',
    season: 'fall',
    category: 'outdoor',
    time_minutes: 30,
    difficulty: 'easy',
    keyed_event: 'first_frost',
    days_offset: -21,
    applies_to: { has_livestock: true },
  },
  {
    key: 'attr.livestock-barn-ventilation',
    title: 'Clean barn vents + fans',
    why:
      'Ammonia, dust, and moisture build up fast in any closed barn. Pull every vent screen, hose it off, and confirm fans actually pull air — pneumonia is preventable.',
    icon: 'Wind',
    tier: 'biannual',
    season: 'spring',
    category: 'outdoor',
    time_minutes: 60,
    difficulty: 'moderate',
    applies_to: { has_livestock: true },
  },
  {
    key: 'attr.livestock-fence-walk',
    title: 'Walk every fence line',
    why:
      'Sag, loose staples, downed sections from a fallen branch. Quarterly walk — slowly — and carry pliers + spare staples. Catches escape routes before the animals do.',
    icon: 'Footprints',
    tier: 'quarterly',
    season: 'any',
    category: 'outdoor',
    time_minutes: 60,
    difficulty: 'moderate',
    applies_to: { has_livestock: true },
  },
  {
    key: 'attr.livestock-feed-pest-control',
    title: 'Feed-room pest control',
    why:
      'Rodents in feed = contamination + a vet bill. Sealed metal cans, glue boards along walls, and a quarterly clean-out of spilled grain breaks the cycle.',
    icon: 'Bug',
    tier: 'quarterly',
    season: 'any',
    category: 'outdoor',
    time_minutes: 30,
    difficulty: 'easy',
    applies_to: { has_livestock: true },
  },
  {
    key: 'attr.livestock-manure-drainage',
    title: 'Check manure runoff + drainage',
    why:
      'Most counties have rules about ag runoff to streams, and your neighbors notice fast. A walk after the first big rain shows where you need more bedding or a swale.',
    icon: 'Droplets',
    tier: 'biannual',
    season: 'spring',
    category: 'outdoor',
    time_minutes: 30,
    difficulty: 'easy',
    applies_to: { has_livestock: true },
  },

  // — SHORT-TERM CONTRACTORS / SERVICE PEOPLE ——————————————
  {
    key: 'attr.contractors-rotate-codes',
    title: 'Rotate door + garage codes',
    why:
      'Cleaners, dog walkers, pool techs, contractors — they all churn. Rotating the shared code every quarter (or after any provider change) tightens the loop.',
    icon: 'KeyRound',
    tier: 'quarterly',
    season: 'any',
    category: 'systems',
    time_minutes: 10,
    difficulty: 'easy',
    applies_to: { has_short_term_contractors: true },
  },
  {
    key: 'attr.contractors-lockbox-battery',
    title: 'Service the lockbox / smart lock battery',
    why:
      'A dead smart lock at 7 AM means the cleaner can’t get in and you’re fielding texts mid-meeting. Annual battery swap + spare in the drawer.',
    icon: 'Battery',
    tier: 'annual',
    season: 'spring',
    category: 'systems',
    time_minutes: 10,
    difficulty: 'easy',
    applies_to: { has_short_term_contractors: true },
  },
  {
    key: 'attr.contractors-alarm-hygiene',
    title: 'Review alarm user codes',
    why:
      'Old cleaner, last year’s contractor, an ex-roommate — they all probably still have a code. Print the user list and delete anyone who no longer needs it.',
    icon: 'ShieldCheck',
    tier: 'biannual',
    season: 'spring',
    category: 'systems',
    time_minutes: 15,
    difficulty: 'easy',
    applies_to: { has_short_term_contractors: true },
  },
  {
    key: 'attr.contractors-key-inventory',
    title: 'Physical key + remote inventory',
    why:
      'Garage remotes, gate fobs, mailbox keys, spare house keys. Count them, label them, and confirm every provider has the right one — and only the right one.',
    icon: 'KeyRound',
    tier: 'annual',
    season: 'spring',
    category: 'systems',
    time_minutes: 20,
    difficulty: 'easy',
    applies_to: { has_short_term_contractors: true },
  },
];

export default attributeRules;
